import { useMemo } from 'react'

import { nutrimaticLabels } from '@/nutrimatic'
import { isLiquidityEnabledForPair } from '../../../../config/marketPolicy'
import { MintField as Field } from '../../../../domain/liquidity'
import type { AddLiquidityControllerResult } from '../../../../features/liquidity/useAddLiquidityController'
import type { RemoveLiquidityControllerResult } from '../../../../features/liquidity/useRemoveLiquidityController'
import { useAppWeb3 } from '../../../../hooks'
import type { NutrimaticMobileNoticeVariant } from '../../../swap/nutrimatic/mobile/NutrimaticMobileNotice'
import { wrappedCurrency } from '../../../../utils/wrappedCurrency'

export type LiquidityMobileNotice = {
  variant: NutrimaticMobileNoticeVariant
  title: string
  description?: string
}

export function useAddLiquidityMobileNotice(
  controller: AddLiquidityControllerResult,
): LiquidityMobileNotice | null {
  const { chainId } = useAppWeb3()
  const { currencies, noLiquidity } = controller

  return useMemo(() => {
    const tokenA = wrappedCurrency(currencies[Field.CURRENCY_A], chainId)
    const tokenB = wrappedCurrency(currencies[Field.CURRENCY_B], chainId)

    if (tokenA && tokenB && chainId && !isLiquidityEnabledForPair(chainId, tokenA, tokenB, 'add')) {
      return {
        variant: 'blocked',
        title: nutrimaticLabels.mobileSimple.liquidityAddBlockedTitle,
        description: nutrimaticLabels.mobileSimple.liquidityAddBlockedHint,
      }
    }

    if (noLiquidity && currencies[Field.CURRENCY_A] && currencies[Field.CURRENCY_B]) {
      return {
        variant: 'info',
        title: nutrimaticLabels.mobileSimple.firstPoolBanner,
        description: nutrimaticLabels.mobileSimple.firstPoolHint,
      }
    }

    return null
  }, [chainId, currencies, noLiquidity])
}

export function useRemoveLiquidityMobileNotice(
  controller: RemoveLiquidityControllerResult,
): LiquidityMobileNotice | null {
  const { chainId } = useAppWeb3()
  const { currencyA, currencyB } = controller

  return useMemo(() => {
    const tokenA = wrappedCurrency(currencyA, chainId)
    const tokenB = wrappedCurrency(currencyB, chainId)

    if (tokenA && tokenB && chainId && !isLiquidityEnabledForPair(chainId, tokenA, tokenB, 'remove')) {
      return {
        variant: 'blocked',
        title: nutrimaticLabels.mobileSimple.liquidityRemoveBlockedTitle,
        description: nutrimaticLabels.mobileSimple.liquidityRemoveBlockedHint,
      }
    }

    return null
  }, [chainId, currencyA, currencyB])
}
